import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: Date;
  type: 'page-load' | 'api-call' | 'render' | 'custom';
  metadata?: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class PerformanceService {
  private readonly metrics$ = new BehaviorSubject<PerformanceMetric[]>([]);
  private readonly activeTimings: Map<string, number> = new Map();

  readonly metrics = this.metrics$.asObservable();

  /**
   * Start timing an operation
   */
  startTiming(name: string): void {
    this.activeTimings.set(name, performance.now());
  }

  /**
   * End timing and record metric
   */
  endTiming(name: string, type: PerformanceMetric['type'] = 'custom', metadata?: Record<string, any>): number {
    const startTime = this.activeTimings.get(name);
    if (!startTime) {
      console.warn(`No start time found for ${name}`);
      return 0;
    }

    const duration = performance.now() - startTime;
    this.activeTimings.delete(name);

    this.recordMetric({
      name,
      value: duration,
      timestamp: new Date(),
      type,
      metadata,
    });

    return duration;
  }

  /**
   * Record a custom metric
   */
  recordMetric(metric: PerformanceMetric): void {
    const current = this.metrics$.value;
    const updated = [...current, metric].slice(-1000); // Keep last 1000 metrics
    this.metrics$.next(updated);

    // Log slow operations
    if (metric.value > 1000 && metric.type === 'api-call') {
      console.warn(`Slow API call: ${metric.name} took ${metric.value.toFixed(2)}ms`, metric.metadata);
    }

    if (metric.value > 100 && metric.type === 'render') {
      console.warn(`Slow render: ${metric.name} took ${metric.value.toFixed(2)}ms`);
    }
  }

  /**
   * Measure page load time
   */
  measurePageLoad(pageName: string): void {
    if (typeof window === 'undefined' || !window.performance) return;

    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (navigation) {
        const loadTime = navigation.loadEventEnd - navigation.fetchStart;
        this.recordMetric({
          name: `page-load:${pageName}`,
          value: loadTime,
          timestamp: new Date(),
          type: 'page-load',
          metadata: {
            domContentLoaded: navigation.domContentLoadedEventEnd - navigation.fetchStart,
            firstPaint: this.getFirstPaint(),
            firstContentfulPaint: this.getFirstContentfulPaint(),
          },
        });
      }
    });
  }

  /**
   * Measure API call performance
   */
  measureApiCall(apiName: string): () => void {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      this.recordMetric({
        name: `api:${apiName}`,
        value: duration,
        timestamp: new Date(),
        type: 'api-call',
        metadata: { endpoint: apiName },
      });
    };
  }

  /**
   * Get first paint time
   */
  private getFirstPaint(): number | undefined {
    const paintEntries = performance.getEntriesByType('paint');
    const fpEntry = paintEntries.find(entry => entry.name === 'first-paint');
    return fpEntry ? fpEntry.startTime : undefined;
  }

  /**
   * Get first contentful paint time
   */
  private getFirstContentfulPaint(): number | undefined {
    const paintEntries = performance.getEntriesByType('paint');
    const fcpEntry = paintEntries.find(entry => entry.name === 'first-contentful-paint');
    return fcpEntry ? fcpEntry.startTime : undefined;
  }

  /**
   * Get metrics by type
   */
  getMetricsByType(type: PerformanceMetric['type']): PerformanceMetric[] {
    return this.metrics$.value.filter(m => m.type === type);
  }

  /**
   * Get average metric value by name
   */
  getAverageMetric(name: string): number {
    const metrics = this.metrics$.value.filter(m => m.name === name);
    if (metrics.length === 0) return 0;

    const sum = metrics.reduce((acc, m) => acc + m.value, 0);
    return sum / metrics.length;
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics$.next([]);
    this.activeTimings.clear();
  }

  /**
   * Get performance summary
   */
  getSummary(): {
    totalMetrics: number;
    averageApiCallTime: number;
    averagePageLoadTime: number;
    averageRenderTime: number;
  } {
    const metrics = this.metrics$.value;
    const apiCalls = this.getMetricsByType('api-call');
    const pageLoads = this.getMetricsByType('page-load');
    const renders = this.getMetricsByType('render');

    return {
      totalMetrics: metrics.length,
      averageApiCallTime: apiCalls.length > 0
        ? apiCalls.reduce((sum, m) => sum + m.value, 0) / apiCalls.length
        : 0,
      averagePageLoadTime: pageLoads.length > 0
        ? pageLoads.reduce((sum, m) => sum + m.value, 0) / pageLoads.length
        : 0,
      averageRenderTime: renders.length > 0
        ? renders.reduce((sum, m) => sum + m.value, 0) / renders.length
        : 0,
    };
  }

  /**
   * Initialize performance monitoring
   */
  initialize(): void {
    if (typeof window === 'undefined' || !window.performance) {
      console.warn('Performance API not available');
      return;
    }

    // Measure initial page load
    if (document.readyState === 'complete') {
      this.measurePageLoad(window.location.pathname);
    } else {
      window.addEventListener('load', () => {
        this.measurePageLoad(window.location.pathname);
      });
    }

    // Monitor Web Vitals
    this.monitorWebVitals();
  }

  /**
   * Monitor Web Vitals (LCP, FID, CLS)
   */
  private monitorWebVitals(): void {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1] as any;
          this.recordMetric({
            name: 'web-vital:lcp',
            value: lastEntry.renderTime || lastEntry.loadTime,
            timestamp: new Date(),
            type: 'page-load',
            metadata: { element: lastEntry.element?.tagName },
          });
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        // LCP not supported
      }

      // First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            this.recordMetric({
              name: 'web-vital:fid',
              value: entry.processingStart - entry.startTime,
              timestamp: new Date(),
              type: 'custom',
              metadata: { eventType: entry.name },
            });
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // FID not supported
      }

      // Cumulative Layout Shift (CLS)
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.recordMetric({
            name: 'web-vital:cls',
            value: clsValue,
            timestamp: new Date(),
            type: 'render',
          });
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        // CLS not supported
      }
    }
  }
}

